import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
} from 'react';
import { Step3Props } from './types';
import {
    validateStep3,
    validatePlasticPackagingSmall,
    validatePlasticPackagingLarge,
    validateMetalPackaging,
    validateMetalPackagingFormat,
    validatePlasticPackagingLargeFormat,
    validatePlasticPackagingSmallFormat,
} from '../Validation';
import { useTranslation } from "react-i18next";
import { Step3ValueModel } from "@apps/CompanyRegistrations/RegisterFlow/Forms/ProducerForm/types";
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors } from "@sharedComponents/PantaForm/utils";
import Icon from "@sharedComponents/Icon";
import InputText from "@sharedComponents/InputText";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'

const Step3: FunctionComponent<Step3Props> = props => {
    const { t } = useTranslation()
    const [stepValues, setStepValues] = useState<Step3ValueModel>({})
    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])
    const allErrors = getAllErrors(formErrors)

    const rehydrate = (values: any) => {
        if (values) {
            // Rehydrate form values when switching between steps,
            // or when a saved registration is opened
            setStepValues({
                ...stepValues,
                metalPackaging: props.values.metalPackaging || '',
                plasticPackagingSmall: props.values.plasticPackagingSmall || '',
                plasticPackagingLarge: props.values.plasticPackagingLarge || '',
            })
        }
    }

    useEffect(() => {
        // When rehydrate key toggles, regardless what value it has, reload values.
        rehydrate(props.values)
    }, [props.rehydrateFormKey])

    const nextButtonText = (
        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
            <span>{t('button.next')}</span>
            <Icon iconName="arrow-right" />
        </span>
    )

    const nextStep = () => {
        validateStep3(stepValues, formErrors).then(
            (errors: FormErrorType[]) => {
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else {
                    setFormErrors([])
                    props.nextStep && props.nextStep()
                }
            }
        )
    }

    const prevStep = () => {
        props.prevStep && props.prevStep()
    }

    const onCancel = () => {
        setFormErrors([])
        props.onCancel && props.onCancel()
    }

    const cancelButtonText = (
        <span className='buttons-wrapper__button-text'>
            <Icon iconName="close" />
            <span>{t('button.cancel')}</span>
        </span>
    )

    const prevButtonText = (
        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--prev'>
            <Icon iconName="arrow-left" />
            <span>{t('button.previous')}</span>
        </span>
    )

    const onChangeMetalPackaging = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, metalPackaging: value })
        props.onChangeValues && props.onChangeValues({ metalPackaging: value })

        let errors = validateMetalPackaging(value, formErrors)
        errors = validateMetalPackagingFormat(value, formErrors)
        setFormErrors(errors)
    }

    const onChangePlasticPackagingSm = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, plasticPackagingSmall: value })
        props.onChangeValues &&
            props.onChangeValues({ plasticPackagingSmall: value })

        let errors = validatePlasticPackagingSmall(value, formErrors)
        errors = validatePlasticPackagingSmallFormat(value, formErrors)
        setFormErrors(errors)
    }

    const onChangePlasticPackagingLg = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, plasticPackagingLarge: value })
        props.onChangeValues &&
            props.onChangeValues({ plasticPackagingLarge: value })

        let errors = validatePlasticPackagingLarge(value, formErrors)
        errors = validatePlasticPackagingLargeFormat(value, formErrors)
        setFormErrors(errors)
    }

    return (
        <div className='producer-form form-step-wrapper form-step-wrapper--wide'>
            <h3>{t('companyRegistration.stepLabel.step3')}</h3>
            <div className='producer-form__content' dangerouslySetInnerHTML={{__html: props.cmsContent}}>
            </div>
            <div className='producer-form__fields'>
                <div className='producer-form__field'>
                    <InputText
                        id="metal-packaging"
                        label={t('companyRegistration.metalPackaging')}
                        value={stepValues.metalPackaging || ''}
                        onChange={onChangeMetalPackaging}
                        autoComplete="off"
                    />
                </div>

                <div className='producer-form__field'>
                    <InputText
                        id="plastic-packaging-sm"
                        label={t('companyRegistration.plasticPackagingSm')}
                        value={stepValues.plasticPackagingSmall || ''}
                        onChange={onChangePlasticPackagingSm}
                        autoComplete="off"
                    />
                </div>

                <div className='producer-form__field'>
                    <InputText
                        id="plastic-packaging-lg"
                        label={t('companyRegistration.plasticPackagingLg')}
                        value={stepValues.plasticPackagingLarge || ''}
                        onChange={onChangePlasticPackagingLg}
                        autoComplete="off"
                    />
                </div>
            </div>
            {allErrors.length > 0 && (
                <div className='producer-form__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={nextButtonText}
                    onClick={nextStep}
                />
                <Button
                    type={ButtonType.PRIMARY}
                    className="prev-button"
                    text={prevButtonText}
                    onClick={prevStep}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={cancelButtonText}
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step3